import { fetchMyVisits, type MyXpResponse, type ProfileVisit } from './socialApi';

export type VenueScores = {
  enabled: true;
  hopper_score: number;
  live_score: number;
  live_score_today: number;
  live_score_7d: number;
  live_score_30d: number;
  live_unique_visits_today: number;
  live_unique_visits_7d: number;
  live_unique_visits_30d: number;
  live_unique_guests_today: number;
  live_unique_guests_7d: number;
  live_unique_guests_30d: number;
};

export type VenueOpeningHour = {
  day: number;
  open: string | null;
  close: string | null;
  is_closed: boolean;
};

export type LegacyVenueOpeningHours = Partial<Record<
  'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
  { open: string | null; close: string | null; is_closed?: boolean }
>>;

export type VenueLoyaltyTierReadModel = {
  min_local_xp: number;
  display_name: string | null;
  privilege_text: string;
};

export type VenueLoyaltyReadModel = {
  program_enabled: boolean;
  local_xp: number;
  current_tier: VenueLoyaltyTierReadModel | null;
  next_tier: VenueLoyaltyTierReadModel | null;
  xp_to_next: number | null;
  progress_percent: number | null;
};

export type VenueDetailData = {
  id: string;
  name: string | null;
  slug: string | null;
  avatar_url: string | null;
  banner_url: string | null;
  description: string | null;
  opening_hours: VenueOpeningHour[] | LegacyVenueOpeningHours | null;
  opening_status?: 'open' | 'closed' | 'unknown';
  address: string | null;
  latitude: string | null;
  longitude: string | null;
  timezone: string | null;
  city: {
    id: string;
    name: string;
    slug: string;
    timezone: string;
  };
  activity: {
    status: 'active' | 'inactive_unconfirmed';
    confirmed_visits: number;
    unique_guests: number;
    last_visit_at: string | null;
  };
  scores: VenueScores | null;
  my_loyalty: VenueLoyaltyReadModel | null;
  is_subscribed: boolean;
  subscribers_count: number;
  is_saved: boolean;
  saved_count: number;
  friends_who_saved: Array<{
    id: string;
    display_name: string | null;
    avatar_url: string | null;
  }>;
  friends_who_saved_count: number;
};

export type VenuePhoto = {
  id: string;
  url: string;
  thumbnail_url: string | null;
  verified: boolean;
  is_official: boolean;
  created_at: string | null;
  user: null | {
    id: string;
    display_name: string | null;
    avatar_url: string | null;
  };
};

export type VenuePhotosResponse = {
  data: VenuePhoto[];
  meta: {
    next_cursor: string | null;
    has_more: boolean;
  };
};

export type CityVenue = {
  id: string;
  name: string | null;
  avatar_url: string | null;
  address: string | null;
  location: null | {
    latitude: string;
    longitude: string;
  };
  geocode_required: boolean;
};

export type CityVenuesResponse = {
  city: {
    id: string;
    country_code: string;
    name: string;
    slug: string;
    timezone: string;
  };
  items: CityVenue[];
  pagination: {
    limit: number;
    has_more: boolean;
    next_cursor: string | null;
  };
};

export type VenueSubscriptionResult = {
  subscribed: boolean;
  venue_id: string;
  subscribers_count: number;
};

export type VenueClaimContactField = 'contact_phone' | 'contact_email';

export type VenueClaimSubmitResult =
  | { outcome: 'submitted'; claimId: string; claimStatus: string }
  | { outcome: 'duplicate' }
  | { outcome: 'account-exists' }
  | { outcome: 'auth-required' }
  | { outcome: 'validation'; fields: Partial<Record<VenueClaimContactField, string>> }
  | { outcome: 'failed' };

type VenueClaimResponse = {
  data?: {
    claim?: {
      id?: string;
      status?: string;
    };
  };
};

type ApiErrorResponse = {
  error?: {
    code?: string;
    fields?: Record<string, string[]>;
  };
};

type CsrfResponse = {
  csrf_token: string;
};

export class VenueDetailApiError extends Error {
  constructor(public readonly status: number, public readonly code: string | null) {
    super(code ?? `venue_api_${status}`);
    this.name = 'VenueDetailApiError';
  }
}

let csrfToken: string | null = null;

async function ensureCsrf(): Promise<string> {
  if (csrfToken) {
    return csrfToken;
  }

  const response = await fetch('/api/v1/auth/csrf', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`CSRF failed: ${response.status}`);
  }

  const body = (await response.json()) as CsrfResponse;
  if (!body.csrf_token) {
    throw new Error('CSRF token is missing');
  }

  csrfToken = body.csrf_token;
  return csrfToken;
}

async function readJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    let code: string | null = null;
    try {
      const body = await response.json() as { error?: { code?: string } };
      code = typeof body.error?.code === 'string' ? body.error.code : null;
    } catch { /* non-JSON error */ }
    throw new VenueDetailApiError(response.status, code);
  }

  return response.json() as Promise<T>;
}

export function fetchVenueDetail(venueId: string, signal?: AbortSignal): Promise<{ venue: VenueDetailData }> {
  return readJson(`/api/v1/venues/${encodeURIComponent(venueId)}`, signal);
}

export function fetchVenuePhotos(
  venueId: string,
  cursor: string | null = null,
  limit = 12,
  signal?: AbortSignal,
): Promise<VenuePhotosResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    source: 'all',
    sort: 'official_first',
  });
  if (cursor) params.set('cursor', cursor);

  return readJson(
    `/api/v1/venues/${encodeURIComponent(venueId)}/photos?${params.toString()}`,
    signal,
  );
}

export function fetchCityVenues(
  cityReference: string,
  limit = 7,
  cursor: string | null = null,
  signal?: AbortSignal,
): Promise<CityVenuesResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);

  return readJson(
    `/api/v1/cities/${encodeURIComponent(cityReference)}/venues?${params.toString()}`,
    signal,
  );
}

async function mutateSubscription(
  venueId: string,
  method: 'POST' | 'DELETE',
): Promise<VenueSubscriptionResult> {
  const csrf = await ensureCsrf();
  const response = await fetch(`/api/v1/venues/${encodeURIComponent(venueId)}/subscribe`, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-CSRF-TOKEN': csrf,
    },
  });

  if (!response.ok) {
    throw new Error(`Venue subscription failed: ${response.status}`);
  }

  return response.json() as Promise<VenueSubscriptionResult>;
}

export function subscribeVenue(venueId: string): Promise<VenueSubscriptionResult> {
  return mutateSubscription(venueId, 'POST');
}

export function unsubscribeVenue(venueId: string): Promise<VenueSubscriptionResult> {
  return mutateSubscription(venueId, 'DELETE');
}

export async function submitVenueClaim(
  venueId: string,
  contactPhone: string,
  contactEmail: string,
): Promise<VenueClaimSubmitResult> {
  const csrf = await ensureCsrf();
  const response = await fetch(`/api/v1/venues/${encodeURIComponent(venueId)}/claims`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrf,
    },
    body: JSON.stringify({
      contact_phone: contactPhone,
      contact_email: contactEmail,
    }),
  });

  if (response.status === 201) {
    const body = (await response.json()) as VenueClaimResponse;
    const claim = body.data?.claim;

    if (!claim?.id || !claim.status) {
      return { outcome: 'failed' };
    }

    return {
      outcome: 'submitted',
      claimId: claim.id,
      claimStatus: claim.status,
    };
  }

  if (response.status === 401) {
    return { outcome: 'auth-required' };
  }

  if (response.status === 422) {
    try {
      const body = (await response.json()) as ApiErrorResponse;
      const fields = body.error?.fields ?? {};

      return {
        outcome: 'validation',
        fields: {
          contact_phone: fields.contact_phone?.[0],
          contact_email: fields.contact_email?.[0],
        },
      };
    } catch {
      return { outcome: 'failed' };
    }
  }

  if (response.status === 409) {
    let errorCode: string | null = null;

    try {
      const body = (await response.json()) as ApiErrorResponse;
      errorCode = body.error?.code ?? null;
    } catch {
      errorCode = null;
    }

    if (errorCode === 'duplicate_claim') {
      return { outcome: 'duplicate' };
    }

    if (errorCode === 'venue_account_exists') {
      return { outcome: 'account-exists' };
    }
  }

  return { outcome: 'failed' };
}

export async function fetchAllMyVisits(): Promise<ProfileVisit[]> {
  const visits: ProfileVisit[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | null = null;

  do {
    const response = await fetchMyVisits('all', 50, cursor);
    visits.push(...response.visits.items);

    const next = response.visits.pagination.has_more
      ? response.visits.pagination.next_cursor
      : null;

    if (!next || seenCursors.has(next)) {
      cursor = null;
    } else {
      seenCursors.add(next);
      cursor = next;
    }
  } while (cursor);

  return visits;
}

export function venueXpProjection(response: MyXpResponse, venueId: string) {
  return response.xp.venues.find((item) => item.venue.id === venueId) ?? null;
}
